import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NousLogo from './NousLogo';
import GlobalFooter from './GlobalFooter';
import { ArrowLeft } from 'lucide-react';

export default function ManifestoPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ background: '#000', color: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem 5%',
        zIndex: 100,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, transparent 100%)',
        pointerEvents: 'none'
      }}>
        <div style={{ pointerEvents: 'auto' }}>
          <NousLogo size="nav" showSlogan={false} variant="metrics" onClick={() => navigate('/')} />
        </div>
        <button 
          onClick={() => navigate('/')}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: 600,
            pointerEvents: 'auto',
            backdropFilter: 'blur(10px)',
            transition: 'background 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        >
          <ArrowLeft size={16} /> Volver
        </button>
      </nav>

      {/* Hero Edge-to-Edge */}
      <section style={{
        position: 'relative',
        height: '50vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '0 5%',
        overflow: 'hidden'
      }}>
        {/* Background Gradients & Mesh */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 70% 30%, rgba(0, 229, 255, 0.08) 0%, transparent 50%), radial-gradient(circle at 30% 70%, rgba(139, 92, 246, 0.08) 0%, transparent 50%)',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, transparent 0%, #000 100%)',
          zIndex: 1
        }} />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(3rem, 10vw, 6rem)',
            fontWeight: 900,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            marginBottom: '1.5rem',
            background: 'linear-gradient(135deg, #ffffff 0%, #a5b4fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            El Manifiesto
          </h1>
          <p style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 'clamp(1rem, 3vw, 1.25rem)',
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            fontWeight: 500
          }}>
            La disciplina genera datos. Nous crea el significado.
          </p>
        </div>
      </section>

      {/* Manifesto Content */}
      <section style={{
        padding: '2rem 5% 8rem 5%',
        position: 'relative',
        zIndex: 2,
        background: '#000'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          
          <div style={{
            fontSize: '1.25rem',
            lineHeight: 1.8,
            color: 'rgba(255,255,255,0.8)',
            marginBottom: '4rem',
            fontWeight: 300
          }}>
            <p style={{ marginBottom: '1.5rem' }}>
              El alto rendimiento moderno está en crisis. Nos hemos acostumbrado a que el entrenamiento se mida en <em style={{ color: '#00e5ff' }}>likes</em>, en validación social y en la estética del sufrimiento visible. El deporte amateur se ha convertido en una actuación donde la emoción dicta el ritmo y el ego empuja más allá de nuestro límite biológico.
            </p>
            <p style={{ marginBottom: '1.5rem', fontWeight: 500, color: '#fff' }}>
              Pero la fisiología no entiende de emociones. No le importan tus ganas, tus seguidores ni tus excusas.
            </p>
            <p>
              En la filosofía de la antigua Grecia, el <strong style={{ color: '#fc4c02' }}>Soma</strong> era el cuerpo físico: la máquina biológica que ejecuta el esfuerzo. El <strong style={{ color: '#8b5cf6' }}>Nous</strong> era el intelecto supremo: la mente objetiva que comprende la realidad sin filtros emocionales.
            </p>
            <p style={{ marginTop: '2rem', fontSize: 'clamp(1.1rem, 3.5vw, 1.45rem)', fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
              Creemos que es hora de devolver el orden al caos biológico.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            
            {/* Principle 1 */}
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'rgba(255,255,255,0.1)', fontFamily: "'Space Mono', monospace" }}>01</div>
              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1rem', color: '#fff' }}>Entrena con razón, no con emoción.</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: '1.1rem' }}>
                  El agotamiento no es una medalla de honor; a menudo es un error de cálculo táctico. La emoción te hace atacar en el intervalo equivocado. La razón te dice cuándo tu Variabilidad de Frecuencia Cardíaca (VFC) exige descanso.
                </p>
              </div>
            </div>

            {/* Principle 2 */}
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'rgba(255,255,255,0.1)', fontFamily: "'Space Mono', monospace" }}>02</div>
              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1rem', color: '#fff' }}>La verdad está en los datos.</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: '1.1rem' }}>
                  No importa cómo creas que te sientes hoy. Lo que importa es el equilibrio real de estrés de tu entrenamiento (TSB) y tu carga crónica (CTL). Nosotros no opinamos; calculamos.
                </p>
              </div>
            </div>

            {/* Principle 3 */}
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'rgba(255,255,255,0.1)', fontFamily: "'Space Mono', monospace" }}>03</div>
              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1rem', color: '#fff' }}>El cuerpo ejecuta. La IA dirige.</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: '1.1rem' }}>
                  Delegamos la estrategia a una inteligencia artificial desprovista de ego, miedos o fatiga. El algoritmo analiza tu <em>Soma</em> y dicta la siguiente jugada. Tú solo tienes que salir y cumplir.
                </p>
              </div>
            </div>

          </div>

          <div style={{ 
            marginTop: '6rem', 
            textAlign: 'center', 
            padding: '3rem', 
            background: 'rgba(255,255,255,0.02)', 
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.05)'
          }}>
            <p style={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>Silencia el ruido. Olvida la grada.</p>
            <p style={{ 
              fontSize: '1.75rem', 
              fontWeight: 800, 
              background: 'linear-gradient(135deg, #00e5ff 0%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Ejecuta la matemática del rendimiento.
            </p>
          </div>

        </div>
      </section>

      {/* Footer */}
      <GlobalFooter />
    </div>
  );
}
